import { gql } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Dialog, TextField } from "@resource/atlas";
import {
  CreateContentTemplateInModal,
  CreateContentTemplateInModalVariables,
} from "generated/schemaTypes";
import { Controller, useForm } from "react-hook-form";
import { analytics } from "react-hooks/useAnalytics";
import useDynamicDefaultValue from "utils/useDynamicDefaultValue";
import useMutation from "utils/useMutation";
import * as yup from "yup";

export const CREATE_CONTENT_TEMPLATE = gql`
  mutation CreateContentTemplateInModal($input: CreateContentTemplateInput!) {
    createContentTemplate(input: $input) {
      message
      success
      code
      contentTemplate {
        id
        name
      }
    }
  }
`;

export type ContentTemplateToDuplicate = {
  id: string;
  name: string;
};

type CreateContentTemplateModalProps = {
  afterSubmit?: (newContentTemplateId?: string) => void;
  contentTemplateToDuplicate?: ContentTemplateToDuplicate;
  name?: string;
  open: boolean;
  setOpen(open: boolean): void;
};

interface TemplateNameFormData {
  name: string;
}

const templateNameSchema = yup
  .object({
    name: yup.string().required(),
  })
  .required();

function CreateContentTemplateModal({
  afterSubmit,
  contentTemplateToDuplicate,
  open,
  setOpen,
}: CreateContentTemplateModalProps) {
  const {
    control: templateNameControl,
    handleSubmit: templateNameHandleSubmit,
    getValues: templateNameGetValues,
    setValue: templateNameSetValue,
    reset: templateNameResetForm,
    clearErrors,
    formState: { errors: templateNameFormErrors },
  } = useForm<TemplateNameFormData>({
    mode: "onBlur",
    resolver: yupResolver(templateNameSchema),
  });

  useDynamicDefaultValue<TemplateNameFormData>(
    "name",
    contentTemplateToDuplicate?.name
      ? `Copy of ${contentTemplateToDuplicate.name}`
      : "",
    templateNameGetValues,
    templateNameSetValue
  );

  const [createContentTemplate, { loading: isCreatingContentTemplate }] =
    useMutation<
      CreateContentTemplateInModal,
      CreateContentTemplateInModalVariables
    >(CREATE_CONTENT_TEMPLATE);

  const isLoading = isCreatingContentTemplate;

  const onValidSubmit = async () => {
    analytics.track("Create Stage Template Form Submitted");
    const res = await createContentTemplate({
      variables: {
        input: {
          name: templateNameGetValues("name"),
          copyFromContentTemplateId: contentTemplateToDuplicate?.id,
        },
      },
    });
    const newContentTemplateId =
      res.data?.createContentTemplate.contentTemplate?.id;
    setOpen(false);
    afterSubmit?.(newContentTemplateId);
  };
  const onSubmit = templateNameHandleSubmit(onValidSubmit);

  const nameError = templateNameFormErrors.name?.message;

  const onModalOpenChange = (value: boolean) => {
    setOpen(value);
    if (!value) {
      clearErrors();
      templateNameResetForm();
    }
  };

  return (
    <Dialog.Root open={open} setOpen={onModalOpenChange}>
      <Dialog.Content
        size="medium"
        header={{
          title: `${
            contentTemplateToDuplicate ? "Duplicate" : "Create"
          } content template`,
        }}
        footer={{
          leftActions: (
            <Button onClick={() => onModalOpenChange(false)}>Cancel</Button>
          ),
          rightActions: (
            <Button variant="primary" onClick={onSubmit} disabled={isLoading}>
              Submit
            </Button>
          ),
        }}
      >
        <div className="text-body-md">
          Content templates allow you to easily reuse content across jobs,
          helping standardize your process.
        </div>
        <form className="pt-6 pb-1 space-y-6" onSubmit={onSubmit}>
          <Controller
            name="name"
            control={templateNameControl}
            render={({ field }) => (
              <TextField
                {...field}
                errorMessage={nameError}
                validationState={(nameError && "invalid") || undefined}
                autoFocus
                value={field.value}
                label="Template name"
                placeholder="e.g. Phone Screen, Assessment, etc."
                isRequired
              />
            )}
          />
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
}

export default CreateContentTemplateModal;
